import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Validator } from '@block-explorer/entities';
import {
  CaptionText,
  FlexColumn,
  FlexRow,
  SubtitleText,
  SvgIcon,
  TableData,
  TableRow,
} from '@make-software/cspr-design';
import { BodyText, HashLength, Tooltip, Link } from '@block-explorer/ui';
import { Avatar, HashLink, HistoryLink } from '../../../components';
import { VALIDATOR_DETAILS_PATH } from '../../../router/paths';
import { buildRouterPath } from '../../../router/utils';
import { deriveAccountInfo, isValidPublicKey } from '../../../utils/account';
import {
  SHORTENED_CONTRACT_NAME_LENGTH,
  truncateName,
} from '../../../components/action-messages/utils';

const StyledTableRow = styled(TableRow)(({ theme }) =>
  theme.withMedia({
    '&&': {
      ':hover, :active': {
        background: 'inherit',
      },
      ':last-child:after': {
        borderBottom: 0,
      },
    },
  })
);
const StyledTableData = styled(TableData)(({ theme }) =>
  theme.withMedia({
    '&&': {
      padding: '16px 0 0',
      width: '100%',
    },
  })
);

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentLightBlue,
  },
}));

interface RandomValidatorRowProps {
  validator: Validator;
}

const RandomValidatorRow = ({ validator }: RandomValidatorRowProps) => {
  const { t } = useTranslation();

  const accountInfo = deriveAccountInfo(
    validator?.accountInfo || validator?.centralizedAccountInfo
  );

  const path = buildRouterPath(
    VALIDATOR_DETAILS_PATH,
    { id: validator.key },
    undefined
  );

  const keyTooltipCaption = isValidPublicKey(validator.key)
    ? t('Public Key')
    : t('Account hash');

  const logo = accountInfo?.logo;
  const name = accountInfo?.name;
  const csprName = validator.csprName || undefined;
  const key = validator.key;
  const info = validator?.accountInfo?.info;
  const thisNode =
    info?.nodes
      ?.filter((i) => isValidPublicKey(i.public_key))
      .find((i) => i.public_key?.toLowerCase() === key.toLowerCase()) || null;

  const description = thisNode?.description;
  const website = validator?.accountInfo?.url;

  const shortenName = truncateName(
    accountInfo?.name,
    SHORTENED_CONTRACT_NAME_LENGTH
  );
  const shortenedWebsite = truncateName(validator?.accountInfo?.url, 30);

  return (
    <StyledTableRow>
      <StyledTableData fitContent>
        <FlexColumn itemsSpacing={32}>
          <FlexRow align="center" itemsSpacing={16}>
            <HistoryLink href={path}>
              {logo ? (
                <Avatar
                  src={logo}
                  loading={!validator}
                  size={'medium'}
                  alt={'Account logo'}
                />
              ) : (
                <Avatar
                  hash={validator.key}
                  loading={!validator}
                  size={'medium'}
                />
              )}
            </HistoryLink>
            <FlexColumn itemsSpacing={2}>
              {name ? (
                <Tooltip caption={t('Validator Name')} tooltipContent={name}>
                  <SubtitleText size={1} scale={'md'} variation="black" noWrap>
                    {shortenName}
                  </SubtitleText>
                </Tooltip>
              ) : null}
              <Tooltip
                caption={keyTooltipCaption}
                tooltipContent={validator.key}
                additionalBlock={
                  csprName && (
                    <FlexColumn>
                      <CaptionText size={2} variation="gray">
                        {t('CSPR.name')}
                      </CaptionText>
                      <BodyText size={3}>{csprName}</BodyText>
                    </FlexColumn>
                  )
                }
              >
                <BodyText
                  size={3}
                  scale="sm"
                  lineHeight={'xs'}
                  monotype={!csprName}
                >
                  <HashLink
                    href={path}
                    hash={validator.key}
                    csprName={csprName}
                    hashLength={HashLength.TINY}
                    ariaDescription={`Link to Validator page`}
                  />
                </BodyText>
              </Tooltip>
              {website ? (
                <Link color="primaryBlue" href={website} rel="noopener">
                  <Tooltip caption={t('Website')} tooltipContent={website}>
                    <FlexRow align={'center'} gap={8}>
                      <BodyText size={3} scale={'sm'} lineHeight={'sm'}>
                        {shortenedWebsite}
                      </BodyText>
                      <StyledSvgIcon
                        src="assets/icons/ic-website-link.svg"
                        size={14}
                        role={'img'}
                      />
                    </FlexRow>
                  </Tooltip>
                </Link>
              ) : null}
            </FlexColumn>
          </FlexRow>

          {description ? (
            <FlexRow itemsSpacing={6} align={'center'}>
              <BodyText size={3} scale="sm" wordBreak>
                {description}
              </BodyText>
            </FlexRow>
          ) : null}
        </FlexColumn>
      </StyledTableData>
    </StyledTableRow>
  );
};

export default RandomValidatorRow;
